import { css } from "lit";
import { ImperativeElement } from "../view.ts";
import { type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { select } from "d3-selection";
import {
  arc as arcGen,
  type DefaultArcObject,
  pie as pieGen,
  type PieArcDatum,
} from "d3-shape";

const styles = css`
  :host {
    display: block;
    width: 100cqw;
    height: 100cqh;
  }

  #donut-chart {
    width: 100%;
    height: 100%;
  }

  .label {
    font-size: var(--font-size-md);
    text-anchor: middle;
    fill: var(--foreground);
  }
`;

export type Segment = {
  label: string;
  value: number;
  color?: string;
};

export type Arc = DefaultArcObject & {
  data: Segment;
  value: number;
  index: number;
};

export type Model = {
  label: string;
  segments: Segment[];
};

export const model = ({ label = "", segments = [] }: Partial<Model> = {}) => ({
  label,
  segments,
});

@customElement("donut-chart")
export class DonutChart extends ImperativeElement {
  static override styles = [Styles.base, styles];

  @property({ type: Object })
  accessor state: Model = model();

  override update(changedProperties: PropertyValues<this>): void {
    super.update(changedProperties);

    const root = select(this.shadowRoot as unknown as Document);
    const state = this.state;

    if (this.isFirstUpdate) {
      root.append("svg").attr("id", "donut-chart");
    }

    // SVG composition dimensions
    // Note that these are relative to the composition and the SVG itself
    // will scale to the container element.
    const width = 700;
    const height = 700;

    // Sample data
    const data = state.segments;

    const radius = Math.min(width, height) / 2.8;
    const innerRadius = radius * 0.9;

    // Create SVG
    const view = root.select("svg#donut-chart");
    // Viewbox sets the relative scale for the composition. The outer SVG
    // element can freely scale with its context.
    view.attr("viewBox", `0 0 ${width} ${height}`);

    // Wipe out previous composition
    view.select("*").remove();

    const g = view
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    // Create pie generator
    const pie = pieGen<unknown, Segment>().value((d: Segment) => d.value);

    // Create arc generator
    const arc = arcGen().innerRadius(innerRadius).outerRadius(radius);

    // Create label arc
    const labelArc = arcGen()
      .innerRadius(radius * 0.8)
      .outerRadius(radius * 0.8);

    const arcsData = pie(data);

    // Draw arcs
    const arcs = g
      .selectAll(".arc")
      .data(arcsData)
      .join("g")
      .attr("class", "arc");

    arcs
      .append("path")
      .attr("d", (d: PieArcDatum<Segment>) => arc(d as Arc))
      .attr("class", "arc-path")
      .attr("fill", (d: PieArcDatum<Segment>) => d.data.color ?? "#fff")
      .style(
        "filter",
        (d: PieArcDatum<Segment>) =>
          `drop-shadow(0 0 24px ${d.data.color ?? "#fff"})`,
      );

    // Add labels
    arcs
      .append("text")
      .attr("class", "label")
      .attr(
        "transform",
        (d: PieArcDatum<Segment>) =>
          `translate(${labelArc.centroid(d as Arc)})`,
      )
      .text((d: PieArcDatum<Segment>) => d.data.label)
      .style("opacity", (d: PieArcDatum<Segment>) =>
        d.endAngle - d.startAngle > 0.3 ? 1 : 0,
      );

    // Add center text
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .style("font-size", "var(--font-size-md)")
      .style("font-weight", "600")
      .style("fill", "var(--muted-foreground)")
      .text(state.label);
  }
}
